/**
 * Choosing what volumes, flows and lengths are shown in (D49), for a design or
 * for a project. Each quantity can be set or left to inherit; what it would
 * inherit is named, so "Project (mL)" says what leaving it means. Length has no
 * kind default: left unset all the way up, it is automatic, per value.
 */
import { useId } from "react";
import { liquidUnitChoices, liquidUnits, type DisplayUnitsSetting } from "./units";
import "./workspace.css";

type Quantity = "volume" | "flow_rate" | "length";

/** What each quantity inherits, as the server names it; null length is automatic. */
export interface InheritedUnits {
  volume: string;
  flow_rate: string;
  length: string | null;
}

const QUANTITIES: readonly { key: Quantity; label: string }[] = [
  { key: "volume", label: "Volume" },
  { key: "flow_rate", label: "Flow rate" },
  { key: "length", label: "Length" },
];

const CHOICES = liquidUnitChoices();

function offeredFor(quantity: Quantity) {
  return quantity === "volume" ? CHOICES.volume : quantity === "flow_rate" ? CHOICES.flowRate : CHOICES.length;
}

/** The server's unit name as shown: "uL/min" as "µL/min". */
function labelOf(quantity: Quantity, code: string): string {
  return offeredFor(quantity).find((choice) => choice.code === code)?.label ?? code;
}

export function UnitsPicker({
  value,
  inherited,
  inheritedFrom,
  effective = null,
  disabled = false,
  onChange,
}: {
  /** The setting here: null, or a quantity left out, inherits. */
  value: DisplayUnitsSetting | null | undefined;
  /** What each quantity is when not set here; null where that depends on each design's kind. */
  inherited: InheritedUnits | null;
  /** Where an unset quantity comes from, for the option's label: "Project", "Kind". */
  inheritedFrom: string;
  /** What is in force, as the server resolved it, for the summary; else worked out here. */
  effective?: { volume: string; flow_rate: string; length?: string | null } | null;
  disabled?: boolean;
  onChange: (next: DisplayUnitsSetting | null) => void;
}) {
  const id = useId();
  const own = value ?? {};
  const shown = (quantity: Quantity) => {
    const code = effective?.[quantity] ?? own[quantity] ?? inherited?.[quantity];
    return code ? labelOf(quantity, code) : quantity === "length" ? null : "by kind";
  };
  const set = (quantity: Quantity, code: string) => {
    const next: DisplayUnitsSetting = { ...own, [quantity]: code || null };
    const any = QUANTITIES.some(({ key }) => next[key]);
    onChange(
      any ? { volume: next.volume ?? null, flow_rate: next.flow_rate ?? null, length: next.length ?? null } : null,
    );
  };
  // What leaving a quantity unset means, for its first option.
  const unsetText = (quantity: Quantity) => {
    if (quantity === "length") {
      const from = inherited?.length;
      return from ? `${inheritedFrom} (${labelOf(quantity, from)})` : "Automatic (per value)";
    }
    return inherited ? `${inheritedFrom} (${labelOf(quantity, inherited[quantity])})` : inheritedFrom;
  };
  const length = shown("length");
  return (
    <details className="ws-units">
      <summary title="What volumes, flows and lengths are shown and typed in. Values are stored in SI whatever is chosen.">
        Units: {shown("volume")} · {shown("flow_rate")}
        {length ? ` · ${length}` : ""}
      </summary>
      <div className="ws-units-body" role="group" aria-label="Display units">
        {QUANTITIES.map(({ key, label }) => (
          <label key={key} htmlFor={`${id}-${key}`} className="ws-inline">
            {label}
            <select
              id={`${id}-${key}`}
              value={own[key] ?? ""}
              disabled={disabled}
              onChange={(event) => set(key, event.target.value)}
            >
              <option value="">{unsetText(key)}</option>
              {offeredFor(key).map((choice) => (
                <option key={choice.code} value={choice.code}>
                  {choice.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>
    </details>
  );
}

/** What a design inherits for each quantity: its project's setting, else its kind's default. */
export function inheritedUnits(
  project: DisplayUnitsSetting | null | undefined,
  kind: "tubing_set" | "microfluidic",
): InheritedUnits {
  const fallback = liquidUnits(null, kind);
  const code = (label: string) => label.replace("\u00b5", "u");
  return {
    volume: project?.volume ?? code(fallback.volume.label),
    flow_rate: project?.flow_rate ?? code(fallback.flowRate.label),
    length: project?.length ?? null,
  };
}
